import { LANGUAGE, REQ_SOURCE, isDefined, isEmpty, isOfType } from '@nfctron/nexus-core';

import axios, { type AxiosError, type AxiosInstance } from 'axios';

import packageJson from '@root/config/package.json';

import { type SessionStore } from '@root/features/authentication/stores/sessionStore';

import { languageEnumTickets } from '@root/lib/constants/language';

import { type ApiConfig, type NfctronApiParams, PassApi, type RequestParams } from './generated/pass.api';
import { RootApi } from './generated/root.api';

/**
 * API endpoint URL (e.g. https://api.staging.nfctron.com)
 */
export const API_BASE = process.env.API_BASE || process.env.NEXT_PUBLIC_API_BASE;
if (isEmpty(API_BASE)) throw new Error('Environmental variable "API_BASE" is not set!');

/** global axios configuration */
export const apiClient: AxiosInstance = axios.create({
	baseURL: API_BASE,
});

/**
 * Custom API error class
 * @extends Error
 * @export
 */
export class ApiError extends Error {
	constructor(
		public name: string,
		public status: number = 500,
		public message: string = '',
	) {
		super();
	}
}

/**
 * Common API response for failed requests
 * @export
 */
export interface CommonAPIErrorResponse {
	statusCode?: number;
	message?: string;
	errorText?: string;
	error?: string;
	type?: string;
}

/**
 * Create NFCtron-Agent headers
 * @param reqSource - Request Source ID
 * @param version - Agent version (App version)
 * @returns NFCtron-Agent supported headers
 */
export function createNfctronAgentHeaders(reqSource: REQ_SOURCE, version: string) {
	return {
		'NFCtron-Agent-Version': version,
		'NFCtron-Agent-Source': reqSource,
	} as const;
}

/**
 * Create NFCtron-API request parameters
 * @param options - Request parameters
 * @returns NFCtron-API request parameters
 */
export function createNfctronApiRequestParams(options: Partial<NfctronApiParams>): RequestParams {
	const { reqSource = REQ_SOURCE.WEB, version = packageJson.version, locale = LANGUAGE.CZECH } = options;
	/** prepare request headers */
	const headers: RequestParams['headers'] = {};

	/** add NFCtron-Agent headers */
	const nfctronAgentHeaders = createNfctronAgentHeaders(reqSource, version ?? packageJson.version);
	headers['NFCtron-Agent-Version'] = nfctronAgentHeaders['NFCtron-Agent-Version'];
	headers['NFCtron-Agent-Source'] = nfctronAgentHeaders['NFCtron-Agent-Source'];

	/** add language header */
	const languageId = languageEnumTickets.get(locale, typeof locale === 'string' ? 'code' : 'languageId').languageId;
	if (isDefined(languageId)) headers['NFCtron-Language'] = languageId;

	return {
		headers,
	} satisfies RequestParams;
}

/** request interceptor */
apiClient.interceptors.request.use(
	(config) => {
		/** set default NFCtron-Agent headers */
		/** default source is WEB, however this should be overriden at the request level depending on the context */
		const nfctronAgentHeaders =
			createNfctronApiRequestParams({ reqSource: REQ_SOURCE.WEB, version: packageJson.version, locale: LANGUAGE.CZECH }).headers ?? {};
		config.headers.set('NFCtron-Agent-Version', config.headers.get('NFCtron-Agent-Version') || nfctronAgentHeaders['NFCtron-Agent-Version']);
		config.headers.set('NFCtron-Agent-Source', config.headers.get('NFCtron-Agent-Source') || nfctronAgentHeaders['NFCtron-Agent-Source']);
		config.headers.set('NFCtron-Language', config.headers.get('NFCtron-Language') || nfctronAgentHeaders['NFCtron-Language']);

		/** disable NFCtron-Agent headers if the request is marked as "no agent" */
		if (config.headers.get('X-No-Agent') === 'true') {
			config.headers.delete('NFCtron-Agent-Version');
			config.headers.delete('NFCtron-Agent-Source');
			config.headers.delete('NFCtron-Language');
			config.headers.delete('X-No-Agent');
		}

		return config;
	},
	(error) => Promise.reject(error),
);

/** response interceptor */
apiClient.interceptors.response.use(
	(res) => {
		try {
			const data = res.data;
			if (isOfType(data, ['errorText']) && isDefined(data.errorText) && data.errorText !== 'OK')
				throw new ApiError(data.errorText?.replace('ERROR_', '')?.toLowerCase() || 'api_error', 500);

			return Promise.resolve(res);
		} catch (clientError) {
			return Promise.reject(clientError);
		}
	},
	async (error) => {
		try {
			/** response received */
			if (isOfType<AxiosError<CommonAPIErrorResponse>>(error, ['response']) && isDefined(error.response)) {
				/** throw the error */
				throw new ApiError(
					(error.response.data.type || error.response.data.errorText || error.response.data.error)?.replace('ERROR_', '')?.toLowerCase() ||
						'api_error',
					error.response.status,
					error.response.data.message || error.response.data.errorText,
				);
			}

			/** no response but request was made */
			if (isOfType<AxiosError<CommonAPIErrorResponse>>(error, ['response']) && isDefined(error.request)) {
				throw new ApiError('no_response', error.response?.status ?? 500);
			}

			/** unknown error */
			throw new ApiError('request_fail', error.response?.status ?? 500);
		} catch (clientError) {
			return Promise.reject(clientError);
		}
	},
);

/**
 * Create individual API configuration
 * @returns API configuration
 */
function createApiConfig(): ApiConfig<SessionStore['account']> {
	return {
		instance: apiClient,
		securityWorker: (session) => {
			if (isDefined(session)) return { headers: { Authorization: `Bearer ${session.token}` } };
			return {};
		},
		nfctronApiWorker: (nfctronApiParams) => {
			return createNfctronApiRequestParams(nfctronApiParams);
		},
	};
}

export type ApiSecurityData = SessionStore['account'];

/** Pass API instance */
export const basePassApi = new PassApi<ApiSecurityData>(createApiConfig());
export function createPassApi(apiParams: NfctronApiParams) {
	const apiConfig = createApiConfig();
	const createdPassApi = new PassApi<ApiSecurityData>(apiConfig);
	createdPassApi.setNfctronApiParams(apiParams);
	return createdPassApi;
}

/** Root API instance */
export const baseRootApi = new RootApi<ApiSecurityData>(createApiConfig());
export function createRootApi(apiParams: NfctronApiParams) {
	const apiConfig = createApiConfig();
	const createdRootApi = new RootApi<ApiSecurityData>(apiConfig);
	createdRootApi.setNfctronApiParams(apiParams);
	return createdRootApi;
}
/**
 * Checks if the error is an instance of ApiError and matches the given status code or error type
 * @param error - The error to check
 * @param statusOrType - The status code or error type to match against
 * @returns True if the error is an ApiError instance and matches the given status or type
 */
export function matchesApiError(error: unknown, statusOrType: (number | string) | Array<number | string>): error is ApiError {
	if (!(error instanceof ApiError)) {
		return false;
	}

	const statusOrTypes = Array.isArray(statusOrType) ? statusOrType : [statusOrType];
	return statusOrTypes.some((_statusOrType) => {
		if (typeof _statusOrType === 'number') {
			return error.status === _statusOrType;
		}

		return error.name === _statusOrType;
	});
}
